import { Row, Table, Modal, Space, Typography, Button, Input, Form, Select } from 'antd'
import { useRequest } from 'ahooks'
import React, { memo, useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'
import { Link, useNavigate } from 'react-router-dom'
import { PlusOutlined } from '@ant-design/icons'
import { createUnitSet, deleteUnitSet, getUnitSetList, activeUnitSet } from '@/services/unitSet/unitSet.service'
import { UnitSet } from '@/services/unitSet/unitSet.model'
import useProjectContext from '@/hooks/useProject'

const UnitSetList = memo(() => {
  const { projectId } = useProjectContext()
  const [text, setText] = useState('')
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { data: unitSets = [], refresh: unitSetsRefresh } = useRequest(() => getUnitSetList({projectId }))

  useEffect(() => {
    const target = unitSets.find((item) => item.active)
    const activeId = target?.id
    if (activeId) {
      form.setFieldsValue({ id: activeId })
    }
  }, [unitSets])

  const filteredData = useMemo(() => {
    if (!text) {
      return unitSets
    }
    return unitSets.filter((item) => item.name.toLowerCase().includes(text))
  }, [unitSets, text])

  const toSearch = (e) => {
    const text = (e.target.value || '').toLowerCase()
    setText(text)
  }

  const duplicateClick = async (unitSet) => {
    const units = unitSet.units.map((item) => {
      const { dim, unit, id, ...other } = item
      return {
        ...other,
        dim: dim.id,
        unit: unit.id
      }
    })
    const data = await createUnitSet(
      { projectId },
      {
        name: `${unitSet.name} copy`,
        units
      }
    )
    navigate(`../${data.id}`)
  }

  const columns = [
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Action',
      dataIndex: '',
      width: 250,
      render: (_, record: UnitSet) => (
        <Space>
          <Link to={String(record.id)}>Edit</Link>
          <a
            onClick={() => {
              Modal.confirm({
                content: 'Are you sure to delete?',
                onOk: async () => {
                  await deleteUnitSet({ projectId, id: record.id })
                  unitSetsRefresh()
                }
              })
            }}
          >
            Delete
          </a>
          <a onClick={() => duplicateClick(record)}>Duplicate</a>
        </Space>
      )
    }
  ]
  return (
    <div>
      <Form
        form={form}
        layout='inline'
        onFinish={async (values) => {
          await activeUnitSet({ projectId, id: values.id })
          unitSetsRefresh()
        }}
        autoComplete='off'
        className='mb-2'
      >
        <Form.Item label='Active' name='id'>
          <Select style={{ minWidth: '200px' }}>
            {unitSets.map((unitSet) => (
              <Select.Option key={unitSet.id} value={unitSet.id}>
                {unitSet.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type='primary' htmlType='submit'>
            Save
          </Button>
        </Form.Item>
      </Form>
      <div>
        <Typography.Title level={5}>Unit Set</Typography.Title>
        <Row justify={'space-between'}>
          <Input placeholder='Search' onChange={toSearch} style={{ width: 200 }} allowClear />
          <Link to={'create'}>
            <Button type='primary' icon={<PlusOutlined />}>
              Create
            </Button>
          </Link>
        </Row>

        <div className={classnames('mt-2')}>
          <Table bordered size='small' dataSource={filteredData} rowKey={(record) => record.id} columns={columns} />
        </div>
      </div>
    </div>
  )
})

export default UnitSetList
